<template>
  <div class="site-autocomplete">
    <q-select
      :model-value="modelValue"
      :options="options"
      :label="label"
      :loading="loading"
      :rules="computedRules"
      outlined
      use-input
      emit-value
      map-options
      option-value="id"
      option-label="name"
      input-debounce="300"
      clearable
      @filter="onFilter"
      @update:model-value="onSelect"
    >
      <template #prepend>
        <q-icon name="location_on" />
      </template>

      <template #option="scope">
        <q-item v-bind="scope.itemProps">
          <q-item-section avatar>
            <q-icon name="location_on" color="grey-7" />
          </q-item-section>
          <q-item-section>
            <q-item-label>{{ scope.opt.name }}</q-item-label>
            <q-item-label v-if="scope.opt.address" caption>
              {{ scope.opt.address }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </template>

      <template #selected-item="scope">
        <div class="row items-center q-gutter-sm">
          <span>{{ scope.opt?.name || '' }}</span>
          <q-badge
            v-if="scope.opt?.address"
            color="grey-5"
            :label="scope.opt.address"
          />
        </div>
      </template>

      <template #no-option>
        <q-item>
          <q-item-section class="text-grey">
            {{ searchTerm ? 'No sites found' : 'Start typing to search...' }}
          </q-item-section>
        </q-item>
      </template>

      <template #after>
        <q-btn
          v-if="!modelValue"
          round
          dense
          flat
          icon="add"
          color="primary"
          href="/sites/new"
          target="_blank"
        >
          <q-tooltip>Create New Site</q-tooltip>
        </q-btn>
      </template>
    </q-select>
  </div>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import { api } from '../services/api';

  const props = defineProps({
    modelValue: {
      type: [Number, String],
      default: null,
    },
    label: {
      type: String,
      default: 'Site',
    },
    required: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['update:modelValue', 'selected']);

  // State
  const loading = ref(false);
  const searchTerm = ref('');
  const options = ref([]);
  const selectedSite = ref(null);

  // Computed
  const computedRules = computed(() => {
    if (props.required) {
      return [(val) => !!val || `${props.label} is required`];
    }
    return [];
  });

  // Methods
  async function onFilter(val, update) {
    searchTerm.value = val;

    if (!val || val.length < 1) {
      update(() => {
        options.value = [];
      });
      return;
    }

    loading.value = true;

    try {
      const results = await api.get('/sites/search', { q: val });

      update(() => {
        options.value = Array.isArray(results) ? results : [];
      });
    } catch (_error) {
      update(() => {
        options.value = [];
      });
    } finally {
      loading.value = false;
    }
  }

  function onSelect(value) {
    if (value === null || value === undefined) {
      selectedSite.value = null;
      emit('update:modelValue', null);
      emit('selected', null);
      return;
    }

    const selected = options.value.find((o) => o.id === value);
    selectedSite.value = selected || null;
    emit('update:modelValue', value);
    emit('selected', selected);
  }

  // Watch for external value changes to load site details (edit mode)
  watch(() => props.modelValue, async (newVal) => {
    if (newVal && !selectedSite.value) {
      try {
        const site = await api.get(`/sites/${newVal}`);
        selectedSite.value = site;
        if (!options.value.find((o) => o.id === newVal)) {
          options.value = [site];
        }
      } catch (_error) {
        // Site not found, ignore
      }
    }
  }, { immediate: true });
</script>

<style scoped>
.site-autocomplete {
  width: 100%;
}
</style>
